import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import {
  closeCircleOutline,
  gitPullRequestOutline,
  arrowBackOutline,
  ticketOutline
} from 'ionicons/icons';
import api from '../../services/api';
import ExecutionPipeline from '../../components/shared/ExecutionPipeline';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import './ImplementationDetailsPage.css';

const ImplementationDetailsPage = ({ setExecutionName }) => {
  const { executionId, projectId } = useParams();
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollIntervalRef = React.useRef(null);

  useEffect(() => {
    fetchImplementationDetails();

    // Poll every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchImplementationDetails();
    }, 3000);

    // Cleanup interval on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  // Stop polling when execution reaches terminal state
  useEffect(() => {
    if (execution?.status === 'completed' || execution?.status === 'failed' || execution?.status === 'cancelled') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [execution?.status]);

  const fetchImplementationDetails = async () => {
    try {
      const data = await api.get(`/executions/${executionId}`);
      setExecution(data);
      
      if (setExecutionName) {
        setExecutionName(data.name || data.ticketKey || 'Implementation');
      }
    } catch (error) {
      console.error('Error fetching implementation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define expected steps for IMPLEMENTATION (these will be updated as progress comes in)
  const getImplementationSteps = () => {
    const reportedSteps = execution?.steps || [];
    
    // Define all expected steps for implementation
    const expectedSteps = [
      { name: 'Start Environment', status: 'queued', duration: '', logs: '' },
      { name: 'Clone Repositories', status: 'queued', duration: '', logs: '' },
      { name: 'Install Dependencies', status: 'queued', duration: '', logs: '' },
      { name: 'Detect Dev Command', status: 'queued', duration: '', logs: '' },
      { name: 'Start Dev Server', status: 'queued', duration: '', logs: '' },
      { name: 'Run Cursor Agent Implementation', status: 'queued', duration: '', logs: '' },
      { name: 'Run E2E Tests', status: 'queued', duration: '', logs: '' },
      { name: 'Create Pull Request', status: 'queued', duration: '', logs: '' },
      { name: 'Report Results', status: 'queued', duration: '', logs: '' },
    ];
    
    // Update with reported progress
    const steps = expectedSteps.map(expectedStep => {
      const reportedStep = reportedSteps.find(s => s.name === expectedStep.name);
      return reportedStep ? { ...expectedStep, ...reportedStep } : expectedStep;
    });
    
    // If execution failed, mark in_progress steps as failed and queued/pending as cancelled
    if (execution?.status === 'failed') {
      let failedFound = false;
      steps.forEach(s => {
        if (s.status === 'running' || s.status === 'in_progress') {
          s.status = 'failed';
          failedFound = true;
        } else if (s.status === 'queued' || s.status === 'pending') {
          s.status = 'cancelled';
        }
      });
      if (!failedFound) {
        const firstNonComplete = steps.find(s => s.status !== 'completed' && s.status !== 'success');
        if (firstNonComplete) firstNonComplete.status = 'failed';
      }
    }
    
    return steps;
  };
  
  const steps = getImplementationSteps();

  if (loading) {
    return (
      <div className="implementation-page">
        <div className="implementation-loading">
          <IonSpinner />
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <NotFoundPage
        code="404"
        title="Implementation not found"
        subtitle="This implementation run doesn't exist, or it may have been deleted. Check the URL or browse the project's tickets."
        primaryAction={{ label: 'Back to tickets', to: `/projects/${projectId}/tickets`, icon: arrowBackOutline }}
        secondaryAction={{ label: 'All tickets', to: `/projects/${projectId}/tickets`, icon: ticketOutline }}
      />
    );
  }

  return (
    <div className="implementation-page">
      {execution.prUrl && (
        <div className="implementation-pr">
          <IonIcon icon={gitPullRequestOutline} />
          <a href={execution.prUrl} target="_blank" rel="noopener noreferrer">
            View Pull Request
          </a>
        </div>
      )}

      {/* Execution Pipeline - ALWAYS SHOW */}
      <ExecutionPipeline steps={steps} executionStatus={execution?.status} />

      {execution.error && (
        <div className="implementation-error">
          <IonIcon icon={closeCircleOutline} />
          <div>
            <h4>Error</h4>
            <p>{execution.error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImplementationDetailsPage;
